/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import React, { useEffect } from 'react'
import Layout from '~/components/Layout'
import { useLang } from '~/hooks/useLang'
import InternalHeader from '~/components/Layout/components/InternalHeader'
import InternalContent from '~/components/Layout/components/InternalContent'
import {
  Button,
  Grid,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useWarehouseForm } from '../hooks/useWarehouseForm'
import type { WarehouseFormSteps } from '../interfaces/IWarehouseForm'
import StepActions from '../components/StepActions'
import AssociateManagers from '../components/AssociateManagers'
import AssociateOperators from '../components/AssociateOperators'
import FilterAutocompleteAgency from '~/components/FilterAutocompleteAgency'
import FilterTextField from '~/components/FilterTextField'

export default function Create() {
  const { translate } = useLang()

  const {
    name,
    agency,
    managersList,
    operatorsList,
    activeStep,
    disabledNextButton,
    isSubmitting,
    // functions
    handleCancelButton,
    handleNext,
    handleBack,
    handleReset,
    addManager,
    removeManager,
    addOperator,
    removeOperator,
    handleCreateWarehouse,
    getBasicInfoValidity,
    getManagersValidity,
  } = useWarehouseForm()

  // Função para voltar ao início mantendo os dados
  const handleResetComplete = () => {
    activeStep.set(0)
  }

  // Handler para mudança de nome
  const handleNameChange = (value: string | null) => {
    name.set(value || '')
  }

  // Handler otimizado para mudança de agência
  const handleAgencyChange = (value: number | null) => {
    if (value) {
      agency.set({ id: value } as any)
    } else {
      agency.set(null)
    }
  }

  const currentStep = activeStep.get()

  const steps: ReadonlyArray<WarehouseFormSteps> = [
    {
      id: 0,
      label: (
        <Typography variant="h6">{translate('Basic Information')}</Typography>
      ),
      content: (
        <Grid container xs={12} spacing={2}>
          <Grid item xs={2}>
            <FilterTextField
              label="Nome"
              currentText={name.get()}
              onTextFieldChange={handleNameChange}
              isRequired={true}
              disabled={currentStep > 0}
            />
          </Grid>
          <Grid item xs={2}>
            <FilterAutocompleteAgency
              onAgencyChange={handleAgencyChange}
              disabled={currentStep > 0}
              isRequired={true}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      id: 1,
      label: (
        <Typography variant="h6">
          {translate('Usuários que podem Gerenciar')}
        </Typography>
      ),
      content: (
        <AssociateManagers
          managersList={managersList}
          onAddManager={addManager}
          onRemoveManager={removeManager}
          disabledFields={currentStep > 1}
        />
      ),
    },
    {
      id: 2,
      label: (
        <Typography variant="h6">
          {translate('Usuários que podem Operar')}
        </Typography>
      ),
      content: (
        <AssociateOperators
          operatorsList={operatorsList}
          onAddOperator={addOperator}
          onRemoveOperator={removeOperator}
          disabledFields={currentStep > 2}
        />
      ),
    },
  ]

  const finalStep = currentStep === steps.length

  return (
    <Layout marginLayout={true}>
      <InternalHeader title={translate('Create New Warehouse')} />
      <InternalContent expandButton={false}>
        <Stepper activeStep={currentStep} orientation="vertical">
          {steps.map((step, index) => {
            return (
              <Step
                key={step.id}
                expanded={
                  finalStep ? false : currentStep >= index ? true : false
                }
              >
                <StepLabel>{step.label}</StepLabel>
                <StepContent TransitionProps={{ unmountOnExit: false }}>
                  {step.content}
                  {currentStep === index && (
                    <StepActions
                      handleNext={handleNext}
                      handleBack={handleBack}
                      index={index}
                      getBasicInfoValidity={getBasicInfoValidity}
                      getManagersValidity={getManagersValidity}
                    />
                  )}
                </StepContent>
              </Step>
            )
          })}
        </Stepper>
        {finalStep && (
          <>
            <Typography>
              {translate("All steps completed - you're finished")}
            </Typography>
            <Button onClick={handleResetComplete} sx={{ mt: 1, mr: 1 }}>
              {translate('Reset')}
            </Button>
          </>
        )}
        <Grid
          container
          xs={12}
          justifyContent="flex-end"
          spacing={2}
          sx={{ mt: 2 }}
        >
          <Grid item>
            <Button variant="outlined" onClick={() => handleCancelButton()}>
              {translate('Cancel')}
            </Button>
          </Grid>
          <Grid item>
            <LoadingButton
              variant="contained"
              onClick={handleCreateWarehouse}
              loading={isSubmitting.get()}
              disabled={currentStep !== steps.length}
            >
              {translate('Create')}
            </LoadingButton>
          </Grid>
        </Grid>
      </InternalContent>
    </Layout>
  )
}
